import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { AppealList } from '../components/AppealList';
import {
    api,
    ApiError,
    type AdminQueueResponse,
    type AdminTaskRow,
    type RootWheelPrize,
    type WheelPrizeKind,
    type AdminWithdrawalRow,
    type Role,
    type WithdrawStatus,
    type GateChat,
    type GateStatus,
    newIdempotencyKey,
} from '../lib/api';
import type { TaskSubmissionRow } from '../lib/api';
import type { ViewId } from '../state';

type Props = {
    onNavigate: (id: ViewId) => void;
    role: Role;
};

type Section = 'withdrawals' | 'stats' | 'tasks' | 'gate' | 'root';
type StatusTab = 'all' | WithdrawStatus;
const TABS: ReadonlyArray<{ id: StatusTab; label: string; short: string }> = [
    { id: 'all',       label: 'Все',             short: 'Все' },
    { id: 'pending',   label: 'Новые',           short: 'Новые' },
    { id: 'reviewing', label: 'Рассматривается', short: 'В работе' },
    { id: 'paid',      label: 'Оплаченные',      short: 'Опл.' },
    { id: 'rejected',  label: 'Отклонённые',     short: 'Откл.' },
    { id: 'failed',    label: 'Ошибки',          short: 'Ошб.' },
];

/**
 * Admin / Root entry point.
 *
 * Sections:
 *   Выводы      — withdrawals queue (admin/checker/root)
 *   Статистика  — house totals, balances, deposits, players (admin/root/checker)
 *   Задания     — task moderation queue (admin/root)
 *   Root        — tunables, role mgmt, balance grant, broadcasts, payouts (root only)
 *
 * The withdrawals queue is the part that already existed; the other sections
 * land here so admin/root have one consolidated panel per ТЗ.
 */
export function AdminPanelView({ onNavigate, role }: Props) {
    const sections: Array<{ id: Section; label: string; show: boolean }> = [
        { id: 'withdrawals', label: 'Выводы',     show: true },
        { id: 'stats',       label: 'Статистика', show: true },
        { id: 'tasks',       label: 'Задания',    show: role === 'checker' || role === 'admin' || role === 'root' },
        { id: 'gate',        label: 'Чаты (ОП)',  show: role === 'admin' || role === 'root' },
        { id: 'root',        label: 'Root',       show: role === 'root' },
    ];
    const [section, setSection] = useState<Section>('withdrawals');

    return (
        <div className="view">
            <div className="admin-header">
                <h2 className="text-center fw-900 text-red" style={{ fontSize: 18, letterSpacing: 2 }}>
                    {role === 'root' ? 'ROOT-ПАНЕЛЬ' : 'АДМИН-ПАНЕЛЬ'}
                </h2>
            </div>

            <div className="admin-tabs">
                {sections.filter((s) => s.show).map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`admin-tab ${section === s.id ? 'active' : ''}`}
                        onClick={() => setSection(s.id)}
                    >
                        <span className="admin-tab-label">{s.label}</span>
                    </button>
                ))}
            </div>

            {section === 'withdrawals' && <WithdrawalsSection role={role} />}
            {section === 'stats' && <StatsSection />}
            {section === 'tasks' && <TasksModerationSection role={role} />}
            {section === 'gate' && <GateSection />}
            {section === 'root' && <RootSection />}

            <button type="button" className="back-btn" onClick={() => onNavigate('menu')}>
                В МЕНЮ
            </button>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Withdrawals                                                           */
/* -------------------------------------------------------------------- */

function WithdrawalsSection({ role }: { role: Role }) {
    const [tab, setTab] = useState<StatusTab>('pending');
    const [data, setData] = useState<AdminQueueResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [drawerId, setDrawerId] = useState<number | null>(null);
    const [busyRowId, setBusyRowId] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await api.adminListWithdrawals(tab);
            setData(resp);
        } catch (e) {
            setError(e instanceof ApiError ? `${e.status}: ${e.code}` : (e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { void refresh(); }, [refresh]);

    const drawerRow = useMemo(
        () => data?.items.find((x) => x.id === drawerId) ?? null,
        [data, drawerId],
    );

    const updateStatus = useCallback(
        async (id: number, status: 'reviewing' | 'paid' | 'rejected', reason?: string) => {
            setBusyRowId(id);
            try {
                await api.adminUpdateWithdrawal(id, { status, reason });
                await refresh();
                setDrawerId(null);
            } catch (e) {
                setError(e instanceof ApiError ? `${e.status}: ${e.code}` : (e as Error).message);
            } finally {
                setBusyRowId(null);
            }
        },
        [refresh],
    );

    return (
        <>
            <div className="admin-tabs">
                {TABS.map((t) => {
                    const cnt = data?.statusCounts[t.id] ?? 0;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            className={`admin-tab ${tab === t.id ? 'active' : ''}`}
                            onClick={() => setTab(t.id)}
                        >
                            <span className="admin-tab-label">{t.short}</span>
                            <span className="admin-tab-count">{cnt}</span>
                        </button>
                    );
                })}
            </div>

            {error && (
                <div className="info-card" style={{ borderColor: 'var(--red)' }}>
                    <p className="popup-text" style={{ color: 'var(--red)' }}>Ошибка: {error}</p>
                </div>
            )}

            <div className="admin-list">
                {loading && !data && (
                    <div className="text-muted f-12 text-center" style={{ padding: 24 }}>Загружаем…</div>
                )}
                {data && data.items.length === 0 && !loading && (
                    <div className="text-muted f-12 text-center" style={{ padding: 24 }}>
                        Пусто на этой вкладке.
                    </div>
                )}
                {data?.items.map((row) => (
                    <button
                        key={row.id}
                        type="button"
                        className={`admin-row admin-row-${row.suspicion}`}
                        onClick={() => setDrawerId(row.id)}
                    >
                        <Row row={row} />
                    </button>
                ))}
            </div>

            {drawerRow && (
                <DetailDrawer
                    row={drawerRow}
                    busy={busyRowId === drawerRow.id}
                    canDecide={role === 'admin' || role === 'root'}
                    onClose={() => setDrawerId(null)}
                    onAction={(s, reason) => updateStatus(drawerRow.id, s, reason)}
                />
            )}
        </>
    );
}

function Row({ row }: { row: AdminWithdrawalRow }) {
    const winrate = ratePct(row.user.battlesWon, row.user.battlesLost);
    const handle = row.user.username ? '@' + row.user.username : (row.user.firstName ?? `id ${row.user.id}`);
    return (
        <>
            <div className="admin-row-top">
                <span className="admin-row-handle">{handle}</span>
                <span className="admin-row-amount">{row.amount.toLocaleString('ru-RU')} BSP</span>
            </div>
            <div className="admin-row-meta">
                <span className="admin-method">{methodLabel(row.method)}</span>
                <span className={`admin-status admin-status-${row.status}`}>{statusLabel(row.status)}</span>
            </div>
            <div className="admin-row-stats">
                <span>💰 {row.user.bspBalance.toLocaleString('ru-RU')}</span>
                <span>💳 {row.user.depositedTotal.toLocaleString('ru-RU')}</span>
                <span>🎯 {winrate}</span>
            </div>
            <div className="admin-row-target">
                <span className="text-muted f-12">→ {row.target}</span>
            </div>
        </>
    );
}

function DetailDrawer({
    row, busy, canDecide, onClose, onAction,
}: {
    row: AdminWithdrawalRow;
    busy: boolean;
    canDecide: boolean;
    onClose: () => void;
    onAction: (s: 'reviewing' | 'paid' | 'rejected', reason?: string) => void;
}) {
    const [reason, setReason] = useState('');
    const handle = row.user.username ? '@' + row.user.username : (row.user.firstName ?? `id ${row.user.id}`);
    const isTerminal = row.status === 'paid' || row.status === 'rejected';
    return (
        <Modal title={`Заявка #${row.id}`} onClose={onClose}>
            <div>
                <div className="info-card">
                    <Kv k="Сумма" v={`${row.amount.toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Метод" v={methodLabel(row.method)} />
                    <Kv k="Куда" v={row.target} />
                    <Kv k="Статус" v={statusLabel(row.status)} />
                    <Kv k="Создана" v={fmtDate(row.createdAt)} />
                    {row.reason && <Kv k="Причина" v={row.reason} />}
                </div>
                <div className="info-card">
                    <Kv k="Юзер" v={handle} />
                    <Kv k="ID" v={String(row.user.id)} />
                    <Kv k="С нами с" v={`${fmtDate(row.user.joinedAt)} (${daysSince(row.user.joinedAt)} дн.)`} />
                    <Kv k="Баланс" v={`${row.user.bspBalance.toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Депозиты" v={`${row.user.depositedTotal.toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Уже выведено" v={`${(row.user.withdrawnTotal ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Бонусы" v={`${(row.user.bonusesEarned ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Рефералка" v={`${(row.user.refEarned ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Батлы" v={`${row.user.battlesWon}В / ${row.user.battlesLost}П (${ratePct(row.user.battlesWon, row.user.battlesLost)})`} />
                    {row.suspicion !== 'normal' && (
                        <div className="withdraw-warn" style={{ color: row.suspicion === 'high' ? 'var(--red)' : 'var(--orange)' }}>
                            {row.suspicion === 'high'
                                ? 'Высокий риск: проверь депозиты и винрейт перед оплатой.'
                                : 'Под наблюдение: баланс заметно выше суммы депозитов.'}
                        </div>
                    )}
                </div>
                {canDecide && !isTerminal && (
                    <>
                        <div className="input-group">
                            <label>Причина (обязательно при отклонении)</label>
                            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" className="action-btn btn-outline-gold" disabled={busy} onClick={() => onAction('reviewing')} style={{ flex: 1 }}>В работу</button>
                            <button type="button" className="action-btn btn-gold" disabled={busy} onClick={() => onAction('paid')} style={{ flex: 1 }}>Оплачено</button>
                        </div>
                        <button type="button" className="action-btn btn-red" disabled={busy || reason.trim().length === 0} onClick={() => onAction('rejected', reason.trim())}>
                            Отклонить + вернуть BSP
                        </button>
                    </>
                )}
                {isTerminal && (
                    <p className="popup-text text-muted text-center" style={{ fontSize: 12 }}>Решение по заявке принято.</p>
                )}
            </div>
        </Modal>
    );
}

/* -------------------------------------------------------------------- */
/* Stats                                                                 */
/* -------------------------------------------------------------------- */

const bsp = (n: number) => `${n.toLocaleString('ru-RU')} BSP`;
const signed = (n: number) => `${n > 0 ? '+' : ''}${bsp(n)}`;

/**
 * The house ledger split by origin. The raw sum used to be shown as one
 * number ("Валовый счёт игр" / "Прибыль (house)") and went deep negative,
 * because creating a root task books its whole budget up front — money still
 * in reserve, not lost.
 */
function LedgerRows({ ledger }: { ledger: import('../lib/api').LedgerBreakdown | undefined }) {
    if (!ledger) return null;
    return (
        <div data-testid="ledger-breakdown">
            <Kv k="Игры (касса)" v={signed(ledger.games)} />
            <Kv k="Комиссии с заданий" v={signed(ledger.taskFees)} />
            <Kv k="Задания root — выплачено" v={signed(-ledger.rootTasksSpent)} />
            {ledger.promos !== 0 && <Kv k="Акции (особые задания, бонусы)" v={signed(ledger.promos)} />}
            <Kv k="Итог кассы" v={signed(ledger.net)} />
            <Kv k="Зарезервировано под задания root" v={bsp(ledger.rootTasksReserved)} />
            <p className="text-muted f-12" style={{ margin: '4px 0 6px' }}>
                Резерв — бюджет активных заданий root, ещё не выплаченный исполнителям. Это не убыток:
                неистраченное вернётся в кассу при закрытии или удалении задания.
                Сырая сумма журнала с резервом: {signed(ledger.rawTotal)}.
            </p>
        </div>
    );
}

function StatsSection() {
    const [data, setData] = useState<Awaited<ReturnType<typeof api.adminStats>> | null>(null);
    const [err, setErr] = useState<string | null>(null);
    useEffect(() => {
        api.adminStats().then(setData).catch((e) => setErr(String(e)));
    }, []);
    if (err) return <p className="popup-text" style={{ color: 'var(--red)' }}>{err}</p>;
    if (!data) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;
    return (
        <div className="info-card">
            <Kv k="Игроков" v={data.users.toLocaleString('ru-RU')} />
            <Kv k="Депозитов всего" v={`${data.deposits.toLocaleString('ru-RU')} BSP`} />
            <Kv k="Выплачено выводов" v={`${(data.withdrawnPaid ?? 0).toLocaleString('ru-RU')} BSP`} />
            <Kv k="На балансах юзеров" v={`${data.balances.toLocaleString('ru-RU')} BSP`} />
            <Kv k="Реальная прибыль" v={`${(data.realProfit ?? 0).toLocaleString('ru-RU')} BSP`} />
            <LedgerRows ledger={data.ledger} />
            <Kv k="BIG GIVEAWAY (открытый банк)" v={`${data.bigOpenPot.toLocaleString('ru-RU')} BSP`} />
            <Kv k="FAST банк" v={`${data.fastBank.toLocaleString('ru-RU')} BSP`} />
            <Kv k="Сыгранных батлов" v={String(data.battles)} />
            <Kv k="Открытий BOXES" v={String(data.boxOpens)} />
            <Kv k="Билетов FAST" v={String(data.fastTickets)} />
            <Kv k="Билетов BIG" v={String(data.bigTickets)} />
            <p className="text-muted f-12" style={{ marginTop: 6 }}>
                «Реальная прибыль» = депозиты − выплаты − балансы юзеров − открытые банки.
                Касса считается по <code>house_ledger</code> и включает cut'ы с грантов рута и реф-бонусов,
                поэтому может быть больше реальной.
            </p>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Tasks moderation                                                      */
/* -------------------------------------------------------------------- */

// ── Tabs inside the tasks section ────────────────────────────────────────────

type TasksTab = 'submissions' | 'appeals' | 'tasks';

function TasksModerationSection({ role }: { role: Role }) {
    // FIX FE-8: checkers can only review submissions, not moderate task objects
    const canModerateTasks = role === 'admin' || role === 'root';
    const [tab, setTab] = useState<TasksTab>('submissions');
    return (
        <>
            <div className="admin-subtabs">
                <button type="button"
                    className={`admin-subtab ${tab === 'submissions' ? 'active' : ''}`}
                    onClick={() => setTab('submissions')}>
                    Заявки
                </button>
                <button type="button"
                    className={`admin-subtab ${tab === 'appeals' ? 'active' : ''}`}
                    onClick={() => setTab('appeals')}>
                    Пересмотры
                </button>
                {canModerateTasks && (
                    <button type="button"
                        className={`admin-subtab ${tab === 'tasks' ? 'active' : ''}`}
                        onClick={() => setTab('tasks')}>
                        Задания
                    </button>
                )}
            </div>
            {tab === 'submissions' && <SubmissionsModerationPanel />}
            {tab === 'appeals' && <AppealList mode="staff" />}
            {tab === 'tasks' && canModerateTasks && <TaskObjectsModerationPanel />}
        </>
    );
}
// ── Submissions (user-submitted work) ─────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
    pending:       'На проверке',
    accepted:      'Принято',
    rejected:      'Отклонено',
    tos_violation: 'Нарушение ToS',
};

function SubmissionsModerationPanel() {
    const API_URL = import.meta.env.VITE_API_URL ?? '';
    const [status, setStatus] = useState<'pending' | 'accepted' | 'rejected' | 'tos_violation'>('pending');
    const [items, setItems]   = useState<TaskSubmissionRow[] | null>(null);
    const [total, setTotal]   = useState(0);
    const [busy, setBusy]     = useState<number | null>(null);
    const [err, setErr]       = useState<string | null>(null);
    const [commentFor, setCommentFor] = useState<{ id: number; action: 'reject' | 'tos' } | null>(null);
    const [comment, setComment]       = useState('');
    const [expanded, setExpanded]     = useState<number | null>(null);

    const load = useCallback(async () => {
        setItems(null); setErr(null);
        try {
            const r = await api.adminListSubmissions({ status, limit: 50 });
            setItems(r.items); setTotal(r.total);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        }
    }, [status]);

    useEffect(() => { void load(); }, [load]);

    const act = async (id: number, action: 'accept' | 'reject' | 'tos', comment?: string) => {
        setBusy(id); setErr(null);
        try {
            if (action === 'accept')  await api.adminAcceptSubmission(id, comment);
            if (action === 'reject')  await api.adminRejectSubmission(id, comment ?? '');
            if (action === 'tos')     await api.adminTosSubmission(id, comment);
            setCommentFor(null); setComment('');
            await load();
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    return (
        <div className="admin-list">
            {/* Status filter */}
            <div style={{ display: 'flex', gap: 6, padding: '8px 0', flexWrap: 'wrap' }}>
                {(['pending','accepted','rejected','tos_violation'] as const).map((s) => (
                    <button key={s} type="button"
                        className={`admin-tab ${status === s ? 'active' : ''}`}
                        style={{ fontSize: 11, padding: '4px 10px' }}
                        onClick={() => setStatus(s)}>
                        {STATUS_LABELS[s]}
                    </button>
                ))}
            </div>

            {err && <div className="action-result err">{err}</div>}

            {items === null && <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>}
            {items !== null && items.length === 0 && (
                <p className="text-muted text-center" style={{ padding: 24 }}>
                    Нет заявок со статусом «{STATUS_LABELS[status]}»
                </p>
            )}

            {items?.map((sub) => {
                const isExpanded = expanded === sub.id;
                const isBusy     = busy === sub.id;
                const cf         = commentFor?.id === sub.id ? commentFor.action : null;
                const userName   = sub.user.username
                    ? `@${sub.user.username}`
                    : sub.user.firstName ?? `#${sub.user.id}`;
                const regDate = new Date(sub.user.registeredAt).toLocaleDateString('ru-RU');
                const subDate = new Date(sub.createdAt).toLocaleString('ru-RU');

                return (
                    <div key={sub.id} className="admin-row">
                        {/* Header */}
                        <div className="admin-row-top" onClick={() => setExpanded(isExpanded ? null : sub.id)}
                            style={{ cursor: 'pointer' }}>
                            <span className="admin-row-handle">
                                #{sub.id} — {sub.task.title}
                            </span>
                            <span className="admin-row-amount">{sub.task.reward} BSP</span>
                        </div>

                        {/* Summary row always visible */}
                        <div className="admin-row-meta">
                            <span className="admin-method">{userName}</span>
                            <span className="text-muted f-12">{subDate}</span>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                            <div style={{ marginTop: 8, fontSize: 13 }}>
                                {/* User info */}
                                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                                    <div><b>Пользователь:</b> {userName}</div>
                                    <div><b>ID:</b> {sub.user.id} &nbsp; <b>Роль:</b> {sub.user.role}</div>
                                    <div><b>Зарегистрирован:</b> {regDate}</div>
                                    {sub.user.tosViolationsCount > 0 && (
                                        <div style={{ color: 'var(--red)' }}>
                                            ⚠️ ToS нарушений: <b>{sub.user.tosViolationsCount}</b>
                                        </div>
                                    )}
                                </div>

                                {/* ToS warning */}
                                {sub.task.tosWarning && (
                                    <div style={{ background: 'rgba(255,80,80,0.1)', borderRadius: 6, padding: '6px 10px', marginBottom: 8, color: '#ff8080', fontSize: 12 }}>
                                        ⚠️ {sub.task.tosWarning}
                                    </div>
                                )}

                                {/* User's text response */}
                                {sub.responseText && (
                                    <div style={{ marginBottom: 8 }}>
                                        <b>Ответ пользователя:</b>
                                        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 6, padding: '6px 8px', marginTop: 4, wordBreak: 'break-word' }}>
                                            {sub.responseText}
                                        </div>
                                    </div>
                                )}

                                {/* Screenshot */}
                                {sub.screenshotPath && (
                                    <div style={{ marginBottom: 8 }}>
                                        <b>Скриншот:</b>
                                        <div style={{ marginTop: 6 }}>
                                            <img
                                                src={`${API_URL}/api/uploads/${sub.screenshotPath}`}
                                                alt="screenshot"
                                                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, objectFit: 'contain', border: '1px solid rgba(255,255,255,0.1)' }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Actions — only for pending */}
                                {sub.status === 'pending' && !cf && (
                                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                                        <button type="button" className="action-btn btn-gold"
                                            disabled={isBusy} style={{ flex: 1, minWidth: 90 }}
                                            onClick={() => act(sub.id, 'accept')}>
                                            {isBusy ? '…' : '✓ Принять'}
                                        </button>
                                        <button type="button" className="action-btn btn-red"
                                            disabled={isBusy} style={{ flex: 1, minWidth: 90 }}
                                            onClick={() => { setCommentFor({ id: sub.id, action: 'reject' }); setComment(''); }}>
                                            Отклонить
                                        </button>
                                        <button type="button" className="action-btn btn-mute"
                                            disabled={isBusy} style={{ flex: '0 0 auto', fontSize: 11, padding: '6px 12px' }}
                                            onClick={() => { setCommentFor({ id: sub.id, action: 'tos' }); setComment('Нарушение ToS Telegram'); }}>
                                            ⚠️ ToS
                                        </button>
                                    </div>
                                )}

                                {/* Reject / ToS comment inline form */}
                                {sub.status === 'pending' && cf && (
                                    <div style={{ marginTop: 8 }}>
                                        <div className="input-group">
                                            <label>{cf === 'tos' ? 'Описание нарушения ToS' : 'Причина отказа (увидит исполнитель — по ней он может запросить пересмотр)'}</label>
                                            <input value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                                placeholder={cf === 'tos' ? 'Нарушение ToS Telegram' : 'Например: скриншот не соответствует заданию'}
                                                autoFocus />
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                            <button type="button" className="action-btn btn-mute"
                                                disabled={isBusy} style={{ flex: 1 }}
                                                onClick={() => setCommentFor(null)}>
                                                Назад
                                            </button>
                                            <button type="button"
                                                className={`action-btn ${cf === 'tos' ? 'btn-mute' : 'btn-red'}`}
                                                disabled={isBusy || (cf === 'reject' && comment.trim().length === 0)}
                                                style={{ flex: 1 }}
                                                onClick={() => act(sub.id, cf, comment || undefined)}>
                                                {isBusy ? '…' : cf === 'tos' ? 'Зафиксировать' : 'Отклонить'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Resolved info */}
                                {sub.status !== 'pending' && sub.moderatorComment && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#aaa' }}>
                                        <b>Комментарий модератора:</b> {sub.moderatorComment}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {total > 50 && (
                <p className="text-muted text-center f-12" style={{ padding: 8 }}>
                    Показаны первые 50 из {total}
                </p>
            )}
        </div>
    );
}

// ── Task objects moderation (admin/root only) ─────────────────────────────────

const CHECK_LABELS: Record<string, string> = {
    bot: 'Бот проверяет подписку', word: 'Проверочное слово', screenshot: 'Скриншот',
    manual: 'Вручную', auto: 'Авто', special: 'Особое',
};

/**
 * "После создания отправляется на модерацию нам. Мы можем отклонить без
 * возврата, отклонить с возвратом, Принять. В любом из отклонений также
 * должна быть возможность вписать причину."
 */
function TaskObjectsModerationPanel() {
    const [items, setItems] = useState<AdminTaskRow[] | null>(null);
    const [busy,  setBusy]  = useState<number | null>(null);
    const [err,   setErr]   = useState<string | null>(null);
    const [msg,   setMsg]   = useState<string | null>(null);
    const [rejectFor, setRejectFor] = useState<{ id: number; refund: boolean } | null>(null);
    const [reason,    setReason]    = useState('');

    const refresh = useCallback(async () => {
        try {
            const r = await api.adminListTasks('pending');
            setItems(r.items);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : (e as Error).message);
            setItems([]);
        }
    }, []);
    useEffect(() => { void refresh(); }, [refresh]);

    const approve = async (id: number) => {
        setBusy(id); setErr(null); setMsg(null);
        try {
            await api.adminModerateTask(id, { status: 'approved' });
            setMsg(`Задание #${id} опубликовано.`);
            await refresh();
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : (e as Error).message);
        } finally { setBusy(null); }
    };

    const reject = async () => {
        if (!rejectFor) return;
        const note = reason.trim();
        if (!note) return;
        setBusy(rejectFor.id); setErr(null); setMsg(null);
        try {
            const r = await api.adminModerateTask(rejectFor.id, { status: 'rejected', note, refund: rejectFor.refund });
            setMsg(rejectFor.refund
                ? `Задание #${rejectFor.id} отклонено, создателю возвращено ${r.refunded.toLocaleString('ru-RU')} BSP.`
                : `Задание #${rejectFor.id} отклонено без возврата средств.`);
            setRejectFor(null); setReason('');
            await refresh();
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : (e as Error).message);
        } finally { setBusy(null); }
    };

    if (!items) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;
    return (
        <div className="admin-list">
            {err && <div className="action-result err">{err}</div>}
            {msg && <div className="action-result ok">{msg}</div>}
            {items.length === 0 && <p className="text-muted text-center" style={{ padding: 24 }}>Нет заданий на модерации.</p>}
            {items.map((t) => {
                const showReject = rejectFor?.id === t.id;
                const creator = t.creator.username ? `@${t.creator.username}` : t.creator.firstName ?? `#${t.creator.id}`;
                return (
                    <div key={t.id} className="admin-row" data-task-id={t.id}>
                        <div className="admin-row-top">
                            <span className="admin-row-handle">#{t.id} {t.title}</span>
                            <span className="admin-row-amount">{t.reward} BSP × {t.slotsTotal}</span>
                        </div>
                        <div className="admin-row-meta">
                            <span className="admin-method">{CHECK_LABELS[t.checkType] ?? t.checkType}</span>
                            <span className="text-muted f-12">от {creator} · списано {t.reserved.toLocaleString('ru-RU')} BSP</span>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, display: 'grid', gap: 4 }}>
                            {t.description && <div><b>Описание:</b> {t.description}</div>}
                            {t.instructions && (
                                <div style={{ whiteSpace: 'pre-wrap' }}><b>Инструкции:</b><br />{t.instructions}</div>
                            )}
                            {t.controlWord && <div><b>Проверочное слово:</b> {t.controlWord}</div>}
                            {t.url && <div style={{ overflowWrap: 'anywhere' }}><b>Ссылка:</b> {t.url}</div>}
                            {t.holdHours > 0 && <div><b>Холд:</b> {t.holdHours} ч</div>}
                            {t.isPremiumOnly && <div><b>Только Premium</b></div>}
                        </div>
                        {!showReject ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                                <button type="button" className="action-btn btn-gold"
                                    disabled={busy === t.id} onClick={() => approve(t.id)} style={{ flex: '1 1 100%' }}>
                                    {busy === t.id ? '…' : 'Принять'}
                                </button>
                                <button type="button" className="action-btn btn-red"
                                    disabled={busy === t.id} style={{ flex: 1 }}
                                    onClick={() => { setRejectFor({ id: t.id, refund: true }); setReason(''); }}>
                                    Отклонить с возвратом
                                </button>
                                <button type="button" className="action-btn btn-mute"
                                    disabled={busy === t.id} style={{ flex: 1 }}
                                    onClick={() => { setRejectFor({ id: t.id, refund: false }); setReason(''); }}>
                                    Без возврата
                                </button>
                            </div>
                        ) : (
                            <div style={{ marginTop: 8 }}>
                                <p className="popup-text f-12" style={{ color: rejectFor!.refund ? 'var(--green)' : '#ff8585' }}>
                                    {rejectFor!.refund
                                        ? `Создателю вернутся ${t.reserved.toLocaleString('ru-RU')} BSP.`
                                        : `${t.reserved.toLocaleString('ru-RU')} BSP останутся у проекта — создатель их не получит.`}
                                </p>
                                <div className="input-group">
                                    <label>Причина отказа (увидит создатель)</label>
                                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                                        placeholder="Например: ссылка не работает" autoFocus />
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                    <button type="button" className="action-btn btn-mute"
                                        disabled={busy === t.id} onClick={() => { setRejectFor(null); setReason(''); }} style={{ flex: 1 }}>
                                        Назад
                                    </button>
                                    <button type="button" className="action-btn btn-red"
                                        disabled={busy === t.id || reason.trim().length === 0} onClick={reject} style={{ flex: 1 }}>
                                        {busy === t.id ? '…' : 'Отклонить'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
/* -------------------------------------------------------------------- */
/* Root section                                                          */
/* -------------------------------------------------------------------- */

type RootTab = 'overview' | 'grant' | 'staff' | 'user' | 'broadcast' | 'payouts' | 'payments' | 'deposits' | 'coupons' | 'tasks' | 'settings' | 'boxes' | 'big' | 'wheel';
const ROOT_TABS: ReadonlyArray<{ id: RootTab; label: string }> = [
    { id: 'overview',  label: 'Обзор' },
    { id: 'grant',     label: 'Баланс' },
    { id: 'staff',     label: 'Команда' },
    { id: 'user',      label: 'Юзер' },
    { id: 'broadcast', label: 'Рассылка' },
    { id: 'payouts',   label: 'Расчёт' },
    { id: 'payments',  label: 'Платежи' },
    { id: 'deposits',  label: 'Депозиты' },
    { id: 'coupons',   label: 'Купоны' },
    { id: 'tasks',     label: 'Задания' },
    { id: 'settings',  label: 'Настройки' },
    { id: 'boxes',     label: 'BOXES' },
    { id: 'big',       label: 'BIG' },
    { id: 'wheel',     label: 'Колесо' },
];

/**
 * Sub-tabs inside the root panel. The previous layout stacked 8 cards in a
 * single column, so "Выдача баланса" sat below 600 px of settings + boxes
 * and was easy to miss — admins thought it was missing entirely. The
 * horizontally-scrolling tab bar puts every action one tap away.
 */
function RootSection() {
    const [tab, setTab] = useState<RootTab>('overview');
    return (
        <>
            <div className="admin-subtabs">
                {ROOT_TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`admin-subtab ${tab === t.id ? 'active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            {tab === 'overview'  && <RootOverview />}
            {tab === 'wheel'     && <RootWheel />}
            {tab === 'grant'     && <RootGrant />}
            {tab === 'staff'     && <RootStaff />}
            {tab === 'user'      && <RootUserLookup />}
            {tab === 'broadcast' && <RootBroadcast />}
            {tab === 'payouts'   && <RootPayouts />}
            {tab === 'payments'  && <RootPaymentLogs />}
            {tab === 'deposits'  && <RootDeposits />}
            {tab === 'coupons'   && <RootCoupons />}
            {tab === 'tasks'     && <RootTasks />}
            {tab === 'settings'  && <RootSettings />}
            {tab === 'boxes'     && <RootBoxes />}
            {tab === 'big'       && <RootBigForce />}
        </>
    );
}

const WHEEL_KIND_LABELS: Record<WheelPrizeKind, string> = {
    nothing: 'Ничего', bsp: 'BSP на баланс', deposit_bonus: '+% к депозиту',
};

/**
 * Пул колеса: what can drop and how many sectors each prize takes — its
 * chance is its sectors / all sectors. Thresholds for extra spins live in
 * Настройки → Колесо.
 */
function RootWheel() {
    const [items, setItems] = useState<RootWheelPrize[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState<number | 'new' | null>(null);
    const [kind, setKind] = useState<WheelPrizeKind>('bsp');
    const [value, setValue] = useState('');
    const [count, setCount] = useState('1');

    const load = useCallback(async () => {
        try { setItems((await api.rootWheelPrizes()).items); }
        catch (e) { setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e)); setItems([]); }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const run = async (key: number | 'new', fn: () => Promise<unknown>) => {
        setBusy(key); setErr(null);
        try { await fn(); await load(); }
        catch (e) {
            const code = e instanceof ApiError ? e.code : String(e);
            setErr(code === 'bad_value' ? 'Неверное значение: BSP от 1, бонус от 1 до 100%.'
                : code === 'bad_count' ? 'Секторов: от 1 до 100.' : code);
        } finally { setBusy(null); }
    };

    const add = () => run('new', async () => {
        await api.rootAddWheelPrize({
            kind, value: kind === 'nothing' ? 0 : Number.parseInt(value, 10), count: Number.parseInt(count, 10),
        });
        setValue(''); setCount('1');
    });

    if (!items) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;
    const totalSectors = items.filter((p) => p.enabled).reduce((a, p) => a + p.count, 0);

    return (
        <div className="admin-list">
            {err && <div className="action-result err">{err}</div>}
            <p className="text-muted f-12">
                Шанс приза = его сектора ÷ все сектора ({totalSectors}). Крутить можно только при депозите на этой неделе,
                1 бесплатная крутка в день; доп. крутки — за депозит и рефералов (пороги в Настройки → Колесо).
            </p>
            {items.length === 0 && <p className="text-muted text-center" style={{ padding: 16 }}>Пул пуст — колесо скрыто у игроков.</p>}
            {items.map((p) => (
                <div key={p.id} className="admin-row" data-prize-id={p.id} style={{ opacity: p.enabled ? 1 : 0.55 }}>
                    <div className="admin-row-top">
                        <span className="admin-row-handle">{p.label}</span>
                        <span className="admin-row-amount">{p.enabled ? `${p.chance}%` : 'выкл.'}</span>
                    </div>
                    <div className="admin-row-meta">
                        <span className="admin-method">{WHEEL_KIND_LABELS[p.kind]}</span>
                        <span className="text-muted f-12">выпадал {p.timesWon} раз</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                        <span className="f-12 text-muted">Секторов:</span>
                        <button type="button" className="action-btn btn-mute" style={{ width: 36, padding: 6 }}
                            disabled={busy === p.id || p.count <= 1}
                            onClick={() => run(p.id, () => api.rootEditWheelPrize(p.id, { count: p.count - 1 }))}>−</button>
                        <b data-testid={`prize-count-${p.id}`} style={{ minWidth: 24, textAlign: 'center' }}>{p.count}</b>
                        <button type="button" className="action-btn btn-mute" style={{ width: 36, padding: 6 }}
                            disabled={busy === p.id || p.count >= 100}
                            onClick={() => run(p.id, () => api.rootEditWheelPrize(p.id, { count: p.count + 1 }))}>+</button>
                        <button type="button" className="action-btn btn-mute" style={{ flex: 1, padding: 6, fontSize: 11 }}
                            disabled={busy === p.id}
                            onClick={() => run(p.id, () => api.rootEditWheelPrize(p.id, { enabled: !p.enabled }))}>
                            {p.enabled ? 'Выключить' : 'Включить'}
                        </button>
                        <button type="button" className="action-btn btn-red" style={{ flex: 1, padding: 6, fontSize: 11 }}
                            disabled={busy === p.id}
                            onClick={() => run(p.id, () => api.rootDeleteWheelPrize(p.id))}>
                            Удалить
                        </button>
                    </div>
                </div>
            ))}

            <div className="info-card" style={{ marginTop: 8 }}>
                <h3 className="popup-headline">Добавить приз</h3>
                <div className="input-group">
                    <label>Тип</label>
                    <select value={kind} onChange={(e) => setKind(e.target.value as WheelPrizeKind)}>
                        <option value="bsp">BSP на баланс</option>
                        <option value="deposit_bonus">+% к следующему депозиту</option>
                        <option value="nothing">Ничего</option>
                    </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {kind !== 'nothing' && (
                        <div className="input-group" style={{ flex: 1 }}>
                            <label>{kind === 'bsp' ? 'Сколько BSP' : 'Бонус, %'}</label>
                            <input type="number" inputMode="numeric" min={1} max={kind === 'deposit_bonus' ? 100 : undefined}
                                value={value} onChange={(e) => setValue(e.target.value)} />
                        </div>
                    )}
                    <div className="input-group" style={{ flex: 1 }}>
                        <label>Секторов в пуле</label>
                        <input type="number" inputMode="numeric" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} />
                    </div>
                </div>
                <button type="button" className="action-btn btn-gold" style={{ marginTop: 8 }}
                    disabled={busy === 'new' || (kind !== 'nothing' && !(Number.parseInt(value, 10) >= 1)) || !(Number.parseInt(count, 10) >= 1)}
                    onClick={add}>
                    {busy === 'new' ? '…' : 'Добавить в пул'}
                </button>
            </div>
        </div>
    );
}

function RootOverview() {
    const [data, setData] = useState<Awaited<ReturnType<typeof api.rootOverview>> | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [exportBusy, setExportBusy] = useState<'json' | 'csv' | 'txt' | null>(null);
    const [exportMsg, setExportMsg] = useState<string | null>(null);
    useEffect(() => {
        api.rootOverview()
            .then(setData)
            .catch((e) => setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e)));
    }, []);

    const downloadExport = async (format: 'json' | 'csv' | 'txt') => {
        if (exportBusy) return;
        setExportBusy(format);
        setExportMsg(null);
        try {
            const r = await api.rootUsersExport(format);
            const mime =
                format === 'csv'  ? 'text/csv' :
                format === 'json' ? 'application/json' :
                                    'text/plain';
            const blob = new Blob([r.body], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `users.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setExportMsg(`Скачано ${r.count} пользователей.`);
        } catch (e) {
            setExportMsg(e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message);
        } finally {
            setExportBusy(null);
        }
    };

    if (err) return <div className="action-result err">{err}</div>;
    if (!data) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;
    return (
        <>
            <div className="info-card">
                <h3 className="popup-headline">Обзор</h3>
                <Kv k="Депозиты" v={`${data.deposits} BSP`} />
                <Kv k="На балансах" v={`${data.userBalances} BSP`} />
                <LedgerRows ledger={data.ledger} />
                <Kv k="BIG банк" v={`${data.bigOpenPot} BSP`} />
                <Kv k="FAST банк" v={`${data.fastBank} BSP`} />
                <Kv k="BOXES сделано ставок" v={`${data.boxesStaked} BSP`} />
                <Kv k="BOXES выплачено" v={`${data.boxesPaid} BSP`} />
            </div>

            <div className="info-card">
                <h3 className="popup-headline">Экспорт юзеров</h3>
                <p className="text-muted f-12">
                    Только реальные игроки — без root/admin/checker и анонимов.
                    Подходит для розыгрыша off-platform: id, @username, имя.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="action-btn btn-gold"
                        style={{ flex: '1 1 100%' }}
                        disabled={exportBusy !== null}
                        onClick={() => downloadExport('txt')}
                    >
                        {exportBusy === 'txt' ? '…' : 'СКАЧАТЬ TXT (ТАБЛИЦА)'}
                    </button>
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        style={{ flex: 1 }}
                        disabled={exportBusy !== null}
                        onClick={() => downloadExport('csv')}
                    >
                        {exportBusy === 'csv' ? '…' : 'CSV'}
                    </button>
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        style={{ flex: 1 }}
                        disabled={exportBusy !== null}
                        onClick={() => downloadExport('json')}
                    >
                        {exportBusy === 'json' ? '…' : 'JSON'}
                    </button>
                </div>
                {exportMsg && (
                    <p className="text-muted f-12 text-center" style={{ marginTop: 6 }}>{exportMsg}</p>
                )}
            </div>
        </>
    );
}

/**
 * Settings are grouped by prefix so root sees them organised by what they
 * actually control instead of a 25-row alphabetical wall. Anything outside
 * the named buckets lands in "Прочее".
 */
const SETTING_GROUPS: Array<{ title: string; match: (k: string) => boolean }> = [
    { title: 'Пополнения',    match: (k) => k.startsWith('deposit_') || k.startsWith('bsp_to_usd') },
    { title: 'Выводы',        match: (k) => k.startsWith('withdraw_') },
    { title: 'BOG BATTLE',    match: (k) => k.startsWith('battle_') },
    { title: 'FAST GIVEAWAY', match: (k) => k.startsWith('fast_') },
    { title: 'BIG GIVEAWAY',  match: (k) => k.startsWith('big_') },
    { title: 'Рефералы',      match: (k) => k.startsWith('referral_') },
    { title: 'Задания',       match: (k) => k.startsWith('task_') },
    { title: 'Канал и боты',  match: (k) => k.startsWith('channel_') },
    { title: 'Колесо',        match: (k) => k.startsWith('wheel_') },
];

/** Human-readable labels for the root settings keys. Anything not listed
 *  here falls back to its raw key — keeps new settings discoverable even
 *  before they get a translation. */
const SETTING_LABELS: Record<string, string> = {
    // Колесо
    wheel_deposit_min:   'Депозит для +1 крутки (BSP)',
    wheel_refs_per_spin: 'Рефералов за 1 крутку',
    wheel_bonus_hours:   'Срок бонуса «+% к депозиту» (часов)',
    // Пополнения
    deposit_commission_stars_pct:  'Комиссия при пополнении через Stars (%)',
    deposit_commission_crypto_pct: 'Комиссия при пополнении через CryptoBot (%)',
    bsp_to_usd_per_1000:           'Курс: сколько USD за 1000 BSP',
    // Выводы
    withdraw_min:     'Минимальная сумма вывода (BSP)',
    withdraw_fee_pct: 'Комиссия с вывода (%) — невидима юзеру',
    // BOG BATTLE
    battle_house_pct:    'Комиссия с банка батла в наш карман (%)',
    battle_giveaway_pct: 'Процент с банка батла в BIG GIVEAWAY (%)',
    battle_vip_fee:      'Доплата за VIP-выделение батла (BSP)',
    battle_min_stake:    'Минимальная ставка в батле (BSP)',
    battle_pin_hours:    'На сколько часов VIP-батл закрепляется',
    // FAST GIVEAWAY
    fast_ticket_price:    'Цена билета FAST GIVEAWAY (BSP)',
    fast_house_pct:       'Процент с FAST в наш карман (%)',
    fast_giveaway_pct:    'Процент с FAST в BIG GIVEAWAY (%)',
    fast_win_chance_pct:  'Шанс выигрыша FAST (%)',
    // BIG GIVEAWAY
    big_ticket_price: 'Цена билета BIG (BSP) — не используется (билеты за рефералов)',
    big_round_days:   'Длительность раунда BIG (дни)',
    // Рефералы
    referral_deposit_pct:  'Реферальный бонус с депозита (%)',
    referral_game_pct:     'Реферальный бонус с каждой игры (%)',
    referral_purchase_pct: 'Реферальный бонус с покупки (%)',
    // Задания
    task_hold_price_per_hour: 'Цена за 1 час холда задания (BSP)',
    task_commission_pct:      'Комиссия с заказа задания (%)',
    // Канал и боты
    channel_chat_id: 'Chat ID канала для автопостов (например, -1001234567890)',
    // PiggyBank
    piggy_stake:       'Ставка PiggyBank (BSP)',
    piggy_max_players: 'Сколько игроков нужно для PiggyBank',
    piggy_house_pct:   'Процент с проигравших PiggyBank в наш карман (%)',
};

function RootSettings() {
    const [settings, setSettings] = useState<Record<string, number | string | boolean> | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [savedKey, setSavedKey] = useState<string | null>(null);
    const [errorKey, setErrorKey] = useState<{ key: string; msg: string } | null>(null);
    const refresh = useCallback(() => api.rootSettings().then((r) => setSettings(r.settings)), []);
    useEffect(() => { void refresh(); }, [refresh]);
    if (!settings) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;

    const keys = Object.keys(settings).sort();
    const grouped: Record<string, string[]> = { Прочее: [] };
    for (const g of SETTING_GROUPS) grouped[g.title] = [];
    for (const k of keys) {
        const g = SETTING_GROUPS.find((g) => g.match(k));
        grouped[g ? g.title : 'Прочее']!.push(k);
    }

    const sectionTitles = [...SETTING_GROUPS.map((g) => g.title), 'Прочее'];

    return (
        <div className="info-card">
            <h3 className="popup-headline">Настройки</h3>
            {sectionTitles.map((title) => {
                const sectionKeys = grouped[title] ?? [];
                if (sectionKeys.length === 0) return null;
                return (
                    <details key={title} open={title === SETTING_GROUPS[0]!.title}>
                        <summary
                            style={{
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 900,
                                letterSpacing: 0.5,
                                color: 'var(--accent)',
                                textTransform: 'uppercase',
                                margin: '8px 0 4px',
                            }}
                        >
                            {title} ({sectionKeys.length})
                        </summary>
                        {sectionKeys.map((k) => (
                            <div key={`${k}-${String(settings[k])}`} className="input-group" style={{ marginBottom: 6 }}>
                                <label style={{ fontSize: 11 }}>
                                    {SETTING_LABELS[k] ?? k}
                                </label>
                                <input
                                    /* Key on `${k}-${value}` so a successful save
                                     * re-mounts the input with the fresh
                                     * defaultValue — uncontrolled inputs don't
                                     * pick up defaultValue prop changes
                                     * without re-mount. */
                                    defaultValue={String(settings[k])}
                                    onBlur={async (e) => {
                                        const v = e.target.value;
                                        if (v === String(settings[k])) return;
                                        const num = Number(v);
                                        const value =
                                            v !== '' && Number.isFinite(num) && /^-?\d+(\.\d+)?$/.test(v) ? num : v;
                                        setBusy(k);
                                        setErrorKey(null);
                                        try {
                                            await api.rootSetSetting(k, value);
                                            await refresh();
                                            setSavedKey(k);
                                            window.setTimeout(() => setSavedKey((cur) => cur === k ? null : cur), 2000);
                                        } catch (err) {
                                            setErrorKey({
                                                key: k,
                                                msg: err instanceof ApiError
                                                    ? `${err.status}: ${err.code}${err.detail ? ` — ${err.detail}` : ''}`
                                                    : (err as Error).message,
                                            });
                                        } finally { setBusy(null); }
                                    }}
                                    disabled={busy === k}
                                />
                                {SETTING_LABELS[k] && (
                                    <span className="hint" style={{ fontSize: 9, opacity: 0.5 }}>
                                        {k}{busy === k ? ' · сохраняем…' : ''}
                                        {savedKey === k ? ' · сохранено ✓' : ''}
                                    </span>
                                )}
                                {errorKey?.key === k && (
                                    <span className="hint" style={{ color: 'var(--red)', fontSize: 10 }}>
                                        {errorKey.msg}
                                    </span>
                                )}
                            </div>
                        ))}
                    </details>
                );
            })}
        </div>
    );
}

function RootStaff() {
    const [data, setData] = useState<Awaited<ReturnType<typeof api.rootStaff>> | null>(null);
    const [uid, setUid] = useState('');
    const [chosenRole, setChosenRole] = useState<Role>('admin');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
    const refresh = useCallback(() => api.rootStaff().then(setData).catch(() => {}), []);
    useEffect(() => { void refresh(); }, [refresh]);
    if (!data) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;

    const uidRaw = uid.trim().replace(/^@/, '');
    const uidOk = /^\d{3,}$/.test(uidRaw);
    const canSubmit = uidOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true); setResult(null);
        try {
            await api.rootAssignRole(Number(uidRaw), chosenRole);
            setResult({ kind: 'ok', text: `Роль "${chosenRole}" выдана пользователю ${uidRaw}.` });
            setUid('');
            await refresh();
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Команда</h3>
            {data.items.length === 0 && (
                <p className="text-muted f-12">Пока никого, кроме root.</p>
            )}
            {data.items.map((m) => (
                <Kv key={m.id} k={m.role} v={m.username ? '@' + m.username : (m.first_name ?? `id ${m.id}`)} />
            ))}
            <div className="input-group">
                <label>Telegram ID</label>
                <input
                    value={uid}
                    onChange={(e) => setUid(e.target.value)}
                    placeholder="123456789"
                    inputMode="numeric"
                />
                {uid.trim() !== '' && !uidOk && (
                    <span className="hint" style={{ color: 'var(--red)' }}>
                        Нужен числовой Telegram ID.
                    </span>
                )}
            </div>
            <div className="input-group">
                <label>Роль</label>
                <select value={chosenRole} onChange={(e) => setChosenRole(e.target.value as Role)}>
                    <option value="admin">admin — модерация выводов и заданий</option>
                    <option value="checker">checker — только просмотр выводов</option>
                    <option value="root">root — полный доступ</option>
                    <option value="user">user — снять права</option>
                </select>
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canSubmit}
                onClick={submit}
            >
                {busy ? '…' : 'Применить'}
            </button>
            {result && <div className={`action-result ${result.kind}`}>{result.text}</div>}
        </div>
    );
}

/**
 * Grant / debit a user's BSP balance.
 *
 * Earlier version silently no-op'd when the input didn't match a hand-rolled
 * regex AND swallowed any server error — the admin clicked "Зачислить", got
 * nothing, concluded "не работает". This rewrite shows:
 *   - inline validation hints (ID must be digits, amount must be a signed int)
 *   - a busy state on the button while the request is in flight
 *   - explicit success (green pill with the new balance) or error (red pill
 *     with status code + reason) feedback
 *   - tolerates @username and leading/trailing whitespace in paste-ins
 * The button is enabled only when both fields parse so the admin sees why
 * nothing happens before clicking.
 */
function RootGrant() {
    const [uid, setUid] = useState('');
    const [amt, setAmt] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    const uidRaw = uid.trim().replace(/^@/, '');
    const amtRaw = amt.trim();
    const uidOk = /^\d{3,}$/.test(uidRaw);
    const amtNum = Number(amtRaw);
    const amtOk = /^-?\d+$/.test(amtRaw) && Number.isFinite(amtNum) && amtNum !== 0;
    const canSubmit = uidOk && amtOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setResult(null);
        try {
            const r = await api.rootGrantBalance(Number(uidRaw), amtNum, note.trim() || undefined);
            setResult({
                kind: 'ok',
                text: `${amtNum > 0 ? '+' : ''}${amtNum} BSP зачислено пользователю ${uidRaw}. Новый баланс: ${r.balance.toLocaleString('ru-RU')} BSP.`,
            });
            setAmt('');
            setNote('');
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError
                    ? `Ошибка ${e.status}: ${e.code}`
                    : (e as Error).message || 'Не удалось выполнить запрос',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Выдача баланса</h3>
            <p className="text-muted f-12">
                Положительная сумма зачисляет, отрицательная — списывает.
                Юзеру приходит уведомление с комментарием, балансовая иконка обновится в течение ~10 секунд.
            </p>
            <div className="input-group">
                <label>Telegram ID</label>
                <input
                    value={uid}
                    onChange={(e) => setUid(e.target.value)}
                    placeholder="например, 123456789"
                    inputMode="numeric"
                />
                {uid.trim() !== '' && !uidOk && (
                    <span className="hint" style={{ color: 'var(--red)' }}>
                        ID должен быть числом (минимум 3 цифры). Username не поддерживается — нужен числовой Telegram ID.
                    </span>
                )}
            </div>
            <div className="input-group">
                <label>Сумма BSP (с минусом — списать)</label>
                <input
                    value={amt}
                    onChange={(e) => setAmt(e.target.value)}
                    placeholder="100 или -50"
                    inputMode="numeric"
                />
                {amt.trim() !== '' && !amtOk && (
                    <span className="hint" style={{ color: 'var(--red)' }}>
                        Введите целое число, отличное от нуля (отрицательное — списание).
                    </span>
                )}
            </div>
            <div className="input-group">
                <label>Комментарий (видно юзеру)</label>
                <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="например: возврат за сбой"
                    maxLength={200}
                />
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canSubmit}
                onClick={submit}
            >
                {busy ? '…' : amtOk && amtNum < 0 ? 'Списать' : 'Зачислить'}
            </button>
            {result && (
                <div className={`action-result ${result.kind}`}>{result.text}</div>
            )}
        </div>
    );
}

function RootBroadcast() {
    const [text, setText] = useState('');
    const [channel, setChannel] = useState<'notif' | 'bot' | 'both'>('notif');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    const trimmed = text.trim();
    const canSubmit = trimmed.length > 0 && trimmed.length <= 2000 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true); setResult(null);
        try {
            const r = await api.rootBroadcast(channel, trimmed);
            setResult({ kind: 'ok', text: `Рассылка #${r.id} запущена.` });
            setText('');
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Рассылка</h3>
            <div className="input-group">
                <label>Текст ({trimmed.length}/2000)</label>
                <textarea
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={2000}
                    placeholder="Сообщение всем пользователям…"
                />
            </div>
            <div className="input-group">
                <label>Канал доставки</label>
                <select value={channel} onChange={(e) => setChannel(e.target.value as 'notif' | 'bot' | 'both')}>
                    <option value="notif">Колокольчик в приложении</option>
                    <option value="bot">Личка бота</option>
                    <option value="both">И там и там</option>
                </select>
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canSubmit}
                onClick={submit}
            >
                {busy ? '…' : 'Отправить'}
            </button>
            {result && <div className={`action-result ${result.kind}`}>{result.text}</div>}
        </div>
    );
}

function RootPayouts() {
    const [amt, setAmt] = useState('');
    const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
    const [busy, setBusy] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
    const refresh = useCallback(() => api.rootListPayouts().then((r) => setItems(r.items)).catch(() => {}), []);
    useEffect(() => { void refresh(); }, [refresh]);

    const amtRaw = amt.trim();
    const amtOk = /^\d+$/.test(amtRaw) && Number(amtRaw) > 0;
    const canSubmit = amtOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true); setResult(null);
        try {
            const r = await api.rootRequestPayout(Number(amtRaw));
            setResult({
                kind: 'ok',
                text: `Заявка #${r.id} создана. Нам: ${r.ourShare} BSP (30%), админу: ${r.adminShare} BSP (70%). House остаток: ${r.houseTotal}.`,
            });
            setAmt('');
            await refresh();
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Расчёт периода</h3>
            <p className="text-muted f-12">Доходы делятся 30% нам / 70% админу. 1000 BSP ≈ 12$.</p>
            <div className="input-group">
                <label>Сколько вывести (BSP, из общего house)</label>
                <input
                    value={amt}
                    onChange={(e) => setAmt(e.target.value)}
                    placeholder="1000"
                    inputMode="numeric"
                />
                {amt.trim() !== '' && !amtOk && (
                    <span className="hint" style={{ color: 'var(--red)' }}>
                        Целое положительное число.
                    </span>
                )}
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canSubmit}
                onClick={submit}
            >
                {busy ? '…' : 'Запросить'}
            </button>
            {result && <div className={`action-result ${result.kind}`}>{result.text}</div>}
            <h3 className="popup-headline" style={{ marginTop: 12 }}>История</h3>
            {items.length === 0 && (
                <p className="text-muted f-12 text-center">Периодов ещё не было.</p>
            )}
            {items.map((p) => {
                const id = Number(p.id);
                const status = String(p.status);
                const isOpen = status === 'requested';
                return (
                    <div key={id} className="info-row" style={{ alignItems: 'center' }}>
                        <div style={{ minWidth: 0 }}>
                            <div className="info-label">
                                #{id} · <span style={{ color: isOpen ? 'var(--accent)' : 'var(--green)' }}>
                                    {status === 'paid' ? 'выплачен' : status === 'cancelled' ? 'отменён' : 'открыт'}
                                </span>
                            </div>
                            <div className="text-muted f-12">
                                нам {String(p.our_share)} BSP · админу {String(p.admin_share)} BSP
                            </div>
                        </div>
                        {isOpen && (
                            <button
                                type="button"
                                className="action-btn btn-green"
                                style={{ width: 'auto', padding: '6px 10px', fontSize: 10, flexShrink: 0 }}
                                disabled={busyId === id}
                                onClick={async () => {
                                    setBusyId(id);
                                    try {
                                        await api.rootClosePayout(id);
                                        await refresh();
                                    } finally { setBusyId(null); }
                                }}
                            >
                                {busyId === id ? '…' : 'Закрыть'}
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Unified payments log. Shows real deposits, paid withdrawals and every
 * root-issued balance grant together so we have a single audit trail. Admin
 * stats deliberately exclude grants — this view is the only place they
 * surface in the UI.
 */
function RootPaymentLogs() {
    type Row = Awaited<ReturnType<typeof api.rootPaymentLogs>>['items'][number];
    const [items, setItems] = useState<Row[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [reconBusy, setReconBusy] = useState(false);
    const [reconMsg, setReconMsg] = useState<string | null>(null);

    const reload = () =>
        api.rootPaymentLogs(200)
            .then((r) => setItems(r.items))
            .catch((e) => setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e)));

    useEffect(() => { void reload(); }, []);

    const reconcile = async () => {
        if (reconBusy) return;
        setReconBusy(true); setReconMsg(null);
        try {
            const s = await api.rootReconcileDeposits();
            setReconMsg(`Просканировано: ${s.scanned}, зачислено: ${s.credited}.`);
            await reload();
        } catch (e) {
            setReconMsg(e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message);
        } finally {
            setReconBusy(false);
        }
    };
    if (err) return <div className="action-result err">{err}</div>;
    if (!items) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;
    if (items.length === 0) return <p className="text-muted text-center" style={{ padding: 24 }}>Платежей пока нет.</p>;
    return (
        <div className="info-card">
            <h3 className="popup-headline">Платежи</h3>
            <p className="text-muted f-12">Депозиты, выплаты выводов и зачисления баланса от root — всё одним списком.</p>
            <button
                type="button"
                className="action-btn btn-outline-gold"
                style={{ marginBottom: 8 }}
                disabled={reconBusy}
                onClick={reconcile}
            >
                {reconBusy ? 'СВЕРЯЕМ…' : 'СВЕРИТЬ STARS-ДЕПОЗИТЫ СЕЙЧАС'}
            </button>
            {reconMsg && (
                <p className="text-muted f-12 text-center" style={{ marginBottom: 8 }}>{reconMsg}</p>
            )}
            {items.map((row) => {
                const handle = row.username ? '@' + row.username : (row.firstName ?? `id ${row.userId}`);
                const actor = row.actorUsername
                    ? '@' + row.actorUsername
                    : (row.actorFirstName ?? (row.actorId ? `id ${row.actorId}` : null));
                const label = row.kind === 'deposit' ? 'Депозит'
                    : row.kind === 'withdraw_paid' ? 'Вывод оплачен'
                    : 'Зачисление root';
                const sign = row.kind === 'withdraw_paid' ? '−' : (row.amount >= 0 ? '+' : '−');
                const colorVar = row.kind === 'deposit' ? 'var(--green)'
                    : row.kind === 'withdraw_paid' ? 'var(--red)'
                    : 'var(--accent)';
                return (
                    <div key={`${row.kind}-${row.id}`} className="info-row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="info-label">
                                <span style={{ color: colorVar, fontWeight: 900 }}>{label}</span>
                                {' · '}{handle}
                            </div>
                            <div className="text-muted f-12">
                                {fmtDate(row.createdAt)}
                                {row.method ? ` · ${methodLabel(row.method)}` : ''}
                                {actor ? ` · от ${actor}` : ''}
                                {row.note ? ` · ${row.note}` : ''}
                            </div>
                        </div>
                        <span className="info-value" style={{ color: colorVar, whiteSpace: 'nowrap' }}>
                            {sign}{Math.abs(row.amount).toLocaleString('ru-RU')} BSP
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Coupons admin — create + list. Two kinds:
 *  - bsp: flat BSP credit on redeem
 *  - deposit_pct: % bonus on user's NEXT real deposit
 * Inputs are kept dumb (no calendar widget, no fancy validators) so the
 * panel doesn't grow another 200 LoC. Codes are uppercased server-side.
 */
/**
 * Root-only task registry: every task regardless of status, with a hard
 * delete. Moderation (Задания tab at the top level) only ever shows the
 * `pending` queue, so a task that was already approved — or one created by
 * mistake — had no way out before this.
 *
 * Delete is confirmed inline rather than through window.confirm(): the
 * Telegram WebView doesn't implement confirm()/prompt().
 */
function RootTasks() {
    type Row = Awaited<ReturnType<typeof api.rootListTasks>>['items'][number];
    type Filter = 'all' | 'pending' | 'approved' | 'rejected' | 'closed';
    const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
        { id: 'all',      label: 'Все' },
        { id: 'pending',  label: 'На модерации' },
        { id: 'approved', label: 'Активные' },
        { id: 'rejected', label: 'Отклонённые' },
        { id: 'closed',   label: 'Закрытые' },
    ];

    const [filter, setFilter] = useState<Filter>('all');
    const [items, setItems] = useState<Row[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    const [busy, setBusy] = useState<number | null>(null);
    const [confirmId, setConfirmId] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        setErr(null);
        try {
            const r = await api.rootListTasks(filter);
            setItems(r.items);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        }
    }, [filter]);
    useEffect(() => { void refresh(); }, [refresh]);

    const remove = async (id: number) => {
        setBusy(id); setErr(null); setMsg(null);
        try {
            const r = await api.rootDeleteTask(id);
            setConfirmId(null);
            const where = r.refundedToHouse ? 'в кассу' : 'создателю';
            const extra = [
                r.attemptsRemoved > 0 ? `снято попыток: ${r.attemptsRemoved}` : null,
                r.submissionsRemoved > 0 ? `удалено заявок: ${r.submissionsRemoved}` : null,
            ].filter(Boolean).join(', ');
            setMsg(
                `Задание #${id} удалено. Возврат ${where}: ` +
                `${r.refunded.toLocaleString('ru-RU')} BSP` + (extra ? `, ${extra}.` : '.'),
            );
            await refresh();
        } catch (e) {
            setErr(e instanceof ApiError
                ? (e.code === 'not_found' ? 'Задание уже удалено.' : `${e.status}: ${e.code}`)
                : String(e));
        } finally { setBusy(null); }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Задания</h3>
            <p className="text-muted f-12">
                Удаление снимает задание у всех, кто его ещё не завершил, и возвращает
                создателю неизрасходованный резерв (то, что списалось при заказе,
                минус уже выплаченное исполнителям). Задания, созданные root,
                финансируются из кассы — возврат уходит туда же.
            </p>

            <div className="admin-subtabs" style={{ marginTop: 8 }}>
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`admin-subtab ${filter === f.id ? 'active' : ''}`}
                        onClick={() => { setFilter(f.id); setItems(null); setConfirmId(null); }}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {err && <div className="action-result err">{err}</div>}
            {msg && <div className="action-result">{msg}</div>}

            {!items ? (
                <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>
            ) : items.length === 0 ? (
                <p className="text-muted text-center" style={{ padding: 24 }}>Заданий нет.</p>
            ) : (
                <div className="admin-list" style={{ marginTop: 8 }}>
                    {items.map((t) => (
                        <div key={t.id} className="admin-row">
                            <div className="admin-row-top">
                                <span className="admin-row-handle">#{t.id} {t.title}</span>
                                <span className="admin-row-amount">{t.reward.toLocaleString('ru-RU')} BSP</span>
                            </div>
                            <div className="admin-row-meta">
                                <span className="admin-method">{t.kind}</span>
                                <span className="text-muted f-12">{t.status}</span>
                                <span className="text-muted f-12">
                                    слоты: {t.slotsLeft}/{t.slotsTotal}
                                </span>
                                <span className="text-muted f-12">
                                    в работе: {t.activeAttempts} · выполнено: {t.doneAttempts}
                                    {t.pendingSubmissions > 0 && ` · на проверке: ${t.pendingSubmissions}`}
                                </span>
                            </div>
                            {confirmId === t.id ? (
                                <div style={{ marginTop: 8 }}>
                                    <p className="text-muted f-12">
                                        Удалить безвозвратно?
                                        {t.activeAttempts + t.pendingSubmissions > 0
                                            ? ` Затронет ${t.activeAttempts + t.pendingSubmissions} чел. — им придёт уведомление об отмене.`
                                            : ''}
                                    </p>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                        <button
                                            type="button"
                                            className="action-btn btn-mute"
                                            disabled={busy === t.id}
                                            onClick={() => setConfirmId(null)}
                                            style={{ flex: 1 }}
                                        >
                                            Отмена
                                        </button>
                                        <button
                                            type="button"
                                            className="action-btn btn-red"
                                            disabled={busy === t.id}
                                            onClick={() => remove(t.id)}
                                            style={{ flex: 1 }}
                                        >
                                            {busy === t.id ? '…' : 'Удалить'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="action-btn btn-red"
                                    style={{ marginTop: 8 }}
                                    disabled={busy !== null}
                                    onClick={() => { setConfirmId(t.id); setMsg(null); }}
                                >
                                    Удалить задание
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function RootCoupons() {
    type Row = Awaited<ReturnType<typeof api.rootListCoupons>>['items'][number];
    const [items, setItems] = useState<Row[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const [code, setCode] = useState('');
    const [kind, setKind] = useState<'bsp' | 'deposit_pct'>('bsp');
    const [amount, setAmount] = useState('');
    const [maxUses, setMaxUses] = useState('');
    const [note, setNote] = useState('');

    const refresh = useCallback(async () => {
        try {
            const r = await api.rootListCoupons();
            setItems(r.items);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        }
    }, []);
    useEffect(() => { void refresh(); }, [refresh]);

    const codeOk   = /^[A-Za-z0-9_-]{3,32}$/.test(code.trim());
    const amountN  = Number(amount);
    const amountOk = Number.isFinite(amountN) && amountN > 0;
    const maxUsesN = maxUses.trim() === '' ? null : Number(maxUses);
    const maxUsesOk = maxUses.trim() === '' || (Number.isFinite(maxUsesN!) && maxUsesN! > 0);
    const canSubmit = codeOk && amountOk && maxUsesOk && busy === null;

    const create = async () => {
        if (!canSubmit) return;
        setBusy('create'); setErr(null);
        try {
            await api.rootCreateCoupon({
                code: code.trim(),
                kind,
                amount: amountN,
                maxUses: maxUsesN,
                note: note.trim() || undefined,
            });
            setCode(''); setAmount(''); setMaxUses(''); setNote('');
            await refresh();
        } catch (e) {
            setErr(e instanceof ApiError
                ? (e.code === 'duplicate_code' ? 'Такой код уже есть.' : `${e.status}: ${e.code}`)
                : String(e));
        } finally { setBusy(null); }
    };

    const toggle = async (id: number) => {
        setBusy(`t-${id}`);
        try {
            await api.rootToggleCoupon(id);
            await refresh();
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    return (
        <>
            <div className="info-card">
                <h3 className="popup-headline">Создать купон</h3>
                <p className="text-muted f-12">
                    BSP: разово зачисляет BSP юзеру.
                    % к депозиту: даёт бонус на следующее пополнение.
                </p>
                <div className="input-group">
                    <label>Код (3–32 символа, A–Z, 0–9, _ -)</label>
                    <input
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="WELCOME100"
                        style={{ textTransform: 'uppercase' }}
                    />
                </div>
                <div className="input-group">
                    <label>Тип</label>
                    <select value={kind} onChange={(e) => setKind(e.target.value as 'bsp' | 'deposit_pct')}>
                        <option value="bsp">BSP на баланс — разово</option>
                        <option value="deposit_pct">% к следующему депозиту</option>
                    </select>
                </div>
                <div className="input-group">
                    <label>{kind === 'bsp' ? 'Сумма BSP' : '% к депозиту'}</label>
                    <input
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder={kind === 'bsp' ? '100' : '10'}
                        inputMode="numeric"
                    />
                </div>
                <div className="input-group">
                    <label>Лимит активаций (пусто = без лимита)</label>
                    <input
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value)}
                        placeholder="50"
                        inputMode="numeric"
                    />
                </div>
                <div className="input-group">
                    <label>Комментарий (только для админа)</label>
                    <input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="например: запуск канала Y"
                        maxLength={200}
                    />
                </div>
                <button
                    type="button"
                    className="action-btn btn-gold"
                    disabled={!canSubmit}
                    onClick={create}
                >
                    {busy === 'create' ? '…' : 'СОЗДАТЬ КУПОН'}
                </button>
                {err && <div className="action-result err">{err}</div>}
            </div>

            <div className="info-card">
                <h3 className="popup-headline">Все купоны ({items?.length ?? '…'})</h3>
                {items === null && (
                    <p className="text-muted text-center f-12">Загружаем…</p>
                )}
                {items && items.length === 0 && (
                    <p className="text-muted text-center f-12">Пока что нет купонов.</p>
                )}
                {items && items.map((c) => (
                    <div key={c.id} className="info-row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="info-label">
                                <span style={{ color: c.disabled ? 'var(--text-muted)' : 'var(--accent)' }}>
                                    {c.code}
                                </span>
                                {' · '}
                                {c.kind === 'bsp' ? `+${c.amount} BSP` : `+${c.amount}% к депозиту`}
                            </div>
                            <div className="text-muted f-12">
                                использован {c.uses}{c.maxUses != null ? ` / ${c.maxUses}` : ''}
                                {c.note ? ` · ${c.note}` : ''}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={`action-btn ${c.disabled ? 'btn-green' : 'btn-mute'}`}
                            style={{ width: 'auto', padding: '6px 10px', fontSize: 10, flexShrink: 0 }}
                            disabled={busy === `t-${c.id}`}
                            onClick={() => toggle(c.id)}
                        >
                            {busy === `t-${c.id}` ? '…' : c.disabled ? 'ВКЛ' : 'ВЫКЛ'}
                        </button>
                    </div>
                ))}
            </div>
        </>
    );
}

/**
 * User lookup: enter a Telegram ID, see the full user card including
 * referral tree, lifetime earnings, recent deposits/withdrawals/battles.
 * Closes the "I need to see who invited whom" gap for support / fraud
 * triage that the staff list and grant-balance fields couldn't cover.
 */
function RootUserLookup() {
    type Data = Awaited<ReturnType<typeof api.adminUserDetails>>;
    const [uid, setUid] = useState('');
    const [data, setData] = useState<Data | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const uidRaw = uid.trim().replace(/^@/, '');
    const uidOk = /^\d{3,}$/.test(uidRaw);

    const lookup = async () => {
        if (!uidOk || busy) return;
        setBusy(true); setErr(null); setData(null);
        try {
            const r = await api.adminUserDetails(Number(uidRaw));
            setData(r);
        } catch (e) {
            setErr(e instanceof ApiError
                ? `${e.status}: ${e.code}${e.detail ? ` — ${e.detail}` : ''}`
                : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">Поиск юзера</h3>
            <div className="input-group">
                <label>Telegram ID</label>
                <input
                    value={uid}
                    onChange={(e) => setUid(e.target.value)}
                    placeholder="123456789"
                    inputMode="numeric"
                />
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!uidOk || busy}
                onClick={lookup}
            >
                {busy ? '…' : 'НАЙТИ'}
            </button>
            {err && <div className="action-result err" style={{ marginTop: 6 }}>{err}</div>}

            {data && (
                <>
                    <h3 className="popup-headline" style={{ marginTop: 14 }}>
                        {data.user.username
                            ? '@' + data.user.username
                            : (data.user.first_name ?? `id ${data.user.id}`)}
                        <span className="text-muted f-12" style={{ marginLeft: 6 }}>
                            ({data.user.role})
                        </span>
                    </h3>
                    <Kv k="ID" v={String(data.user.id)} />
                    <Kv k="Баланс" v={`${Number(data.user.bsp_balance).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Депозитов всего" v={`${Number(data.user.deposited_total).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Заработано на рефах" v={`${data.user.ref_earned.toLocaleString('ru-RU')} BSP`} />
                    <Kv k="Батлы" v={`${data.user.battles_won}В / ${data.user.battles_lost}П`} />
                    <Kv k="Зарегистрирован" v={fmtDate(data.user.created_at)} />
                    <Kv k="Последний заход" v={fmtDate(data.user.last_seen_at)} />

                    <h3 className="popup-headline" style={{ marginTop: 14 }}>Рефералка</h3>
                    <Kv k="Реф-код" v={data.referrals.code ?? '—'} />
                    <Kv k="Кого пригласил" v={String(data.referrals.count)} />
                    <Kv
                        k="Заработал (всего)"
                        v={`${data.referrals.earnedTotal.toLocaleString('ru-RU')} BSP`}
                    />
                    <Kv k="… с депозитов" v={`${(data.referrals.earnedByKind.deposit ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="… с игр" v={`${(data.referrals.earnedByKind.game ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv k="… с покупок" v={`${(data.referrals.earnedByKind.purchase ?? 0).toLocaleString('ru-RU')} BSP`} />
                    <Kv
                        k="Кто пригласил"
                        v={data.user.referrer
                            ? (data.user.referrer.username
                                ? '@' + data.user.referrer.username
                                : (data.user.referrer.first_name ?? `id ${data.user.referrer.id}`))
                            : '—'}
                    />

                    {data.referrals.items.length > 0 && (
                        <>
                            <h3 className="popup-headline" style={{ marginTop: 10 }}>
                                Приглашённые ({data.referrals.items.length})
                            </h3>
                            {data.referrals.items.slice(0, 30).map((r) => {
                                const handle = r.username
                                    ? '@' + r.username
                                    : (r.firstName ?? `id ${r.id}`);
                                return (
                                    <div className="info-row" key={r.id} style={{ alignItems: 'flex-start' }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div className="info-label">{handle}</div>
                                            <div className="text-muted f-12">
                                                {fmtDate(r.joinedAt)} · депозитов {r.depositedTotal} BSP · баланс {r.balance} BSP
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

/**
 * Deposits diagnostics + manual credit.
 *
 * Triple-purpose:
 *  1. List every pending_deposits row so root can see what's "in flight"
 *     after a user complains "оплатил Stars, баланс не пришёл".
 *  2. Dump raw getStarTransactions for that bot so we can see whether
 *     Telegram even has the user's payment, and what invoice_payload it
 *     attached to it.
 *  3. Force-credit a specific pending row via /api/root/deposits/credit-manually
 *     — the nuclear option when reconcile keeps missing.
 */
function RootDeposits() {
    type Pending = Awaited<ReturnType<typeof api.rootPendingDeposits>>['items'][number];
    const [pending, setPending] = useState<Pending[] | null>(null);
    const [starTxns, setStarTxns] = useState<unknown[] | null>(null);
    const [txnsErr, setTxnsErr] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        try {
            const r = await api.rootPendingDeposits();
            setPending(r.items);
        } catch (e) {
            setMsg(e instanceof ApiError ? `Ошибка: ${e.status}: ${e.code}` : String(e));
        }
    }, []);

    useEffect(() => { void refresh(); }, [refresh]);

    const fetchStarTxns = async () => {
        setBusy('star-txns'); setTxnsErr(null);
        try {
            const r = await api.rootStarTxns();
            if (r.ok && r.transactions) setStarTxns(r.transactions);
            else { setStarTxns([]); setTxnsErr(r.error ?? 'unknown'); }
        } catch (e) {
            setTxnsErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    const reconcile = async () => {
        setBusy('reconcile'); setMsg(null);
        try {
            const s = await api.rootReconcileDeposits();
            setMsg(
                `Просканировано: ${s.scanned}, зачислено: ${s.credited}.` +
                (s.matchedPayloads.length > 0 ? ` Payloads с invoice: ${s.matchedPayloads.length}.` : '') +
                (s.firstError ? ` Ошибка: ${s.firstError}` : ''),
            );
            await refresh();
        } catch (e) {
            setMsg(e instanceof ApiError ? `Ошибка: ${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    const creditManual = async (id: number) => {
        if (!window.confirm(`Зачислить депозит #${id} вручную?`)) return;
        setBusy(`credit-${id}`); setMsg(null);
        try {
            const r = await api.rootCreditDepositManually(id);
            setMsg(
                r.credited
                    ? `Депозит #${id} зачислен: +${r.amount} BSP юзеру ${r.userId}.`
                    : `Не удалось: ${r.reason ?? 'unknown'}.`,
            );
            await refresh();
        } catch (e) {
            setMsg(e instanceof ApiError ? `Ошибка: ${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    return (
        <>
            <div className="info-card">
                <h3 className="popup-headline">Stars-депозиты — диагностика</h3>
                <p className="text-muted f-12">
                    Если юзер жалуется «оплатил, баланс не пришёл» — здесь видно, что лежит в pending,
                    что Telegram реально знает (getStarTransactions), и можно зачислить вручную.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="action-btn btn-gold"
                        style={{ flex: 1 }}
                        disabled={busy !== null}
                        onClick={reconcile}
                    >
                        {busy === 'reconcile' ? '…' : 'СВЕРИТЬ И ЗАЧИСЛИТЬ'}
                    </button>
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        style={{ flex: 1 }}
                        disabled={busy !== null}
                        onClick={fetchStarTxns}
                    >
                        {busy === 'star-txns' ? '…' : 'STAR ТРАНЗАКЦИИ TG'}
                    </button>
                </div>
                {msg && <p className="text-muted f-12 text-center" style={{ marginTop: 6 }}>{msg}</p>}
            </div>

            <div className="info-card">
                <h3 className="popup-headline">Pending depositов ({pending?.length ?? '…'})</h3>
                {pending === null && (
                    <p className="text-muted text-center f-12">Загружаем…</p>
                )}
                {pending && pending.length === 0 && (
                    <p className="text-muted text-center f-12">Зависших платежей нет.</p>
                )}
                {pending && pending.map((p) => {
                    const handle = p.username ? '@' + p.username : (p.firstName ?? `id ${p.userId}`);
                    return (
                        <div key={p.id} className="info-row" style={{ alignItems: 'flex-start' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="info-label">
                                    #{p.id} · {p.provider} · {handle}
                                </div>
                                <div className="text-muted f-12">
                                    {fmtDate(p.createdAt)} · {p.amount} BSP
                                </div>
                                <div className="text-muted" style={{ fontSize: 9, wordBreak: 'break-all' }}>
                                    payload: {p.payload}
                                </div>
                                {p.chargeId && (
                                    <div className="text-muted" style={{ fontSize: 9, wordBreak: 'break-all' }}>
                                        charge: {p.chargeId}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                className="action-btn btn-green"
                                style={{ width: 'auto', padding: '6px 10px', fontSize: 10, flexShrink: 0 }}
                                disabled={busy === `credit-${p.id}`}
                                onClick={() => creditManual(p.id)}
                            >
                                {busy === `credit-${p.id}` ? '…' : 'ЗАЧИСЛИТЬ'}
                            </button>
                        </div>
                    );
                })}
            </div>

            {starTxns !== null && (
                <div className="info-card">
                    <h3 className="popup-headline">Star транзакции бота ({starTxns.length})</h3>
                    {txnsErr && <p style={{ color: 'var(--red)', fontSize: 11 }}>{txnsErr}</p>}
                    {starTxns.length === 0 && !txnsErr && (
                        <p className="text-muted text-center f-12">
                            Telegram не вернул ни одной звёздной транзакции для этого бота.
                            Скорее всего платежи идут к другому боту (проверь BOT_TOKEN).
                        </p>
                    )}
                    <pre
                        style={{
                            fontSize: 9,
                            color: 'var(--text-muted)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxHeight: 320,
                            overflow: 'auto',
                            background: 'var(--bg-deep)',
                            padding: 8,
                            borderRadius: 6,
                        }}
                    >
                        {JSON.stringify(starTxns, null, 2)}
                    </pre>
                </div>
            )}
        </>
    );
}

/**
 * BSP-BOXES payouts / RTP editor. Each case has fixed cell counts per color
 * (15/8/4/2/1) — only the per-color payout and case cost are tunable. The
 * server recomputes RTP and force-closes the active pool when payouts change
 * so the new mix kicks in on the very next open.
 */
function RootBoxes() {
    const [items, setItems] = useState<Awaited<ReturnType<typeof api.rootBoxes>>['items'] | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const refresh = useCallback(() => api.rootBoxes().then((r) => setItems(r.items)).catch(() => {}), []);
    useEffect(() => { void refresh(); }, [refresh]);
    if (!items) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;

    return (
        <div className="info-card">
            <h3 className="popup-headline">BSP BOXES — RTP / выплаты</h3>
            <p className="text-muted f-12">
                Раскладка пула фиксирована: 15/8/4/2/1 (синий/жёлтый/зелёный/оранжевый/красный).
                Меняешь цену или выплату — текущий пул закрывается, следующий розыг идёт уже по новой матрице.
            </p>
            {items.map((c) => (
                <BoxRow
                    key={c.code}
                    item={c}
                    busy={busy === c.code}
                    onSave={async (cost, payouts, enabled) => {
                        setBusy(c.code);
                        try {
                            await api.rootUpdateBox(c.code, { cost, payouts, enabled });
                            await refresh();
                        } finally { setBusy(null); }
                    }}
                />
            ))}
        </div>
    );
}

type BoxRowItem = Awaited<ReturnType<typeof api.rootBoxes>>['items'][number];

function BoxRow({ item, busy, onSave }: {
    item: BoxRowItem;
    busy: boolean;
    onSave: (
        cost: number,
        payouts: BoxRowItem['payouts'],
        enabled: boolean,
    ) => Promise<void>;
}) {
    const [cost, setCost] = useState(String(item.cost));
    const [enabled, setEnabled] = useState(item.enabled);
    const [p, setP] = useState<BoxRowItem['payouts']>(item.payouts);

    const liveRtp = useMemo(() => {
        const c = Number(cost);
        if (!Number.isFinite(c) || c <= 0) return item.rtp;
        const total =
            item.layout.blue   * p.blue +
            item.layout.yellow * p.yellow +
            item.layout.green  * p.green +
            item.layout.orange * p.orange +
            item.layout.red    * p.red;
        return total / (30 * c);
    }, [cost, p, item.layout, item.rtp]);

    const set = (k: keyof BoxRowItem['payouts'], v: string) => {
        const n = Math.max(0, Math.floor(Number(v) || 0));
        setP({ ...p, [k]: n });
    };

    return (
        <div className="info-card" style={{ borderColor: enabled ? 'var(--border)' : 'var(--red)' }}>
            <div className="info-row">
                <span className="info-label">{item.code} · {item.label}</span>
                <span className="info-value">
                    RTP {(liveRtp * 100).toFixed(1)}% · house {Math.max(0, (1 - liveRtp) * 100).toFixed(1)}%
                </span>
            </div>
            <div className="info-row">
                <span className="info-label">Открытий / выплачено</span>
                <span className="info-value">
                    {item.opens.toLocaleString('ru-RU')} · {item.paid.toLocaleString('ru-RU')} BSP
                </span>
            </div>
            <div className="info-row">
                <span className="info-label">Осталось в пуле</span>
                <span className="info-value">{item.poolLeft} / 30</span>
            </div>
            <div className="input-group">
                <label>Цена открытия</label>
                <input value={cost} onChange={(e) => setCost(e.target.value)} />
            </div>
            <div className="input-group">
                <label>Выплаты по цветам (BSP)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {(['blue','yellow','green','orange','red'] as const).map((k) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                            <span style={{ minWidth: 56, color: tierColor(k) }}>{k} ×{item.layout[k]}</span>
                            <input
                                type="number"
                                value={p[k]}
                                onChange={(e) => set(k, e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </label>
                    ))}
                </div>
            </div>
            <label className="checkbox-row" style={{ justifyContent: 'flex-start', gap: 10 }}>
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                />
                <span className="f-12">Активен (виден игрокам)</span>
            </label>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={busy}
                onClick={() => onSave(Math.max(1, Math.floor(Number(cost) || item.cost)), p, enabled)}
            >
                {busy ? '...' : 'Сохранить'}
            </button>
        </div>
    );
}

function tierColor(k: 'blue'|'yellow'|'green'|'orange'|'red') {
    switch (k) {
        case 'blue':   return 'var(--blue)';
        case 'yellow': return 'var(--accent)';
        case 'green':  return 'var(--green)';
        case 'orange': return 'var(--orange)';
        case 'red':    return 'var(--red)';
    }
}

function RootBigForce() {
    const [roundId, setRoundId] = useState('');
    const [uid, setUid] = useState('');
    const [tier, setTier] = useState<'10' | '5' | '1'>('10');
    const [busyForce, setBusyForce] = useState(false);
    const [busyDraw, setBusyDraw] = useState(false);
    const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    const roundOk = /^\d+$/.test(roundId.trim());
    const uidOk = /^\d{3,}$/.test(uid.trim().replace(/^@/, ''));
    const canForce = roundOk && uidOk && !busyForce;

    const force = async () => {
        if (!canForce) return;
        setBusyForce(true); setResult(null);
        try {
            await api.rootForceBigWin(Number(roundId), Number(uid.trim().replace(/^@/, '')), tier);
            setResult({
                kind: 'ok',
                text: `Победный билет тира ${tier}% закреплён за юзером ${uid.trim()} в раунде ${roundId}.`,
            });
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message,
            });
        } finally {
            setBusyForce(false);
        }
    };

    const draw = async () => {
        if (busyDraw) return;
        setBusyDraw(true); setResult(null);
        try {
            const r = await api.rootDrawBig();
            setResult({ kind: 'ok', text: `Розыгрыш проведён. Банк ${r.pot} BSP. Победителей: ${r.winners.length}.` });
        } catch (e) {
            setResult({
                kind: 'err',
                text: e instanceof ApiError ? `Ошибка ${e.status}: ${e.code}` : (e as Error).message,
            });
        } finally {
            setBusyDraw(false);
        }
    };

    return (
        <div className="info-card">
            <h3 className="popup-headline">BIG GIVEAWAY — выдать билет</h3>
            <div className="input-group">
                <label>Round ID</label>
                <input
                    value={roundId}
                    onChange={(e) => setRoundId(e.target.value)}
                    placeholder="например, 42"
                    inputMode="numeric"
                />
            </div>
            <div className="input-group">
                <label>Telegram ID победителя</label>
                <input
                    value={uid}
                    onChange={(e) => setUid(e.target.value)}
                    placeholder="123456789"
                    inputMode="numeric"
                />
            </div>
            <div className="input-group">
                <label>Тир приза</label>
                <select value={tier} onChange={(e) => setTier(e.target.value as '10' | '5' | '1')}>
                    <option value="10">10% (1 место)</option>
                    <option value="5">5% (5 мест)</option>
                    <option value="1">1% (65 мест)</option>
                </select>
            </div>
            <button
                type="button"
                className="action-btn btn-gold"
                disabled={!canForce}
                onClick={force}
            >
                {busyForce ? '…' : 'Закрепить победителя'}
            </button>
            <button
                type="button"
                className="action-btn btn-outline-gold"
                style={{ marginTop: 6 }}
                disabled={busyDraw}
                onClick={draw}
            >
                {busyDraw ? '…' : 'Провести розыгрыш сейчас'}
            </button>
            {result && <div className={`action-result ${result.kind}`}>{result.text}</div>}
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Chat gate (ОП) — обязательная подписка для входа в чат               */
/* -------------------------------------------------------------------- */

const GATE_ERRORS: Record<string, string> = {
    bad_chat: 'Не похоже на чат. Нужен @username или id вида -100…',
    chat_not_found: 'Чат не найден. Бот должен быть в нём, а username — публичным (или укажите id).',
    not_a_group: 'Это не группа/супергруппа.',
    bot_not_admin: 'Сначала сделайте бота админом чата (право «Удалять сообщения» и «Блокировать пользователей»).',
    bad_channel: 'Не похоже на канал. Нужны @username, ссылка t.me/… или id вида -100…',
    url_required: 'Для канала по id укажите ссылку для кнопки (t.me/… или инвайт).',
    channel_not_found: 'Канал не найден. Для проверки нужен публичный @username или id, и бот — админ канала.',
    bot_not_admin_in_channel: 'Бот не админ этого канала — проверять подписку не сможет. Добавьте бота админом или отключите проверку.',
    verify_needs_public_or_id: 'Инвайт-ссылку нельзя проверить. Отключите проверку или укажите @username / id.',
    too_many_channels: 'Не больше 10 каналов на чат.',
    duplicate: 'Этот канал уже добавлен.',
    forbidden: 'Нет доступа.',
};
const gateErr = (e: unknown) =>
    e instanceof ApiError ? (GATE_ERRORS[e.code] ?? `Ошибка ${e.status}: ${e.code}`) : (e as Error).message;

function GateSection() {
    const [items, setItems] = useState<GateChat[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [chatInput, setChatInput] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { setItems((await api.gateChats()).items); setErr(null); }
        catch (e) { setErr(gateErr(e)); }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const addChat = async () => {
        if (!chatInput.trim() || busy) return;
        setBusy(true); setErr(null);
        try { await api.gateAddChat(chatInput.trim()); setChatInput(''); await load(); }
        catch (e) { setErr(gateErr(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <div className="info-card">
                <h3 className="popup-headline">Обязательная подписка в чатах</h3>
                <p className="text-muted" style={{ fontSize: 12, marginTop: 0 }}>
                    Добавьте бота админом в чат (права: удалять сообщения, блокировать пользователей) —
                    чат появится ниже сам. Или впишите @username / id чата вручную.
                    У каждого бота свой список: чаты не пересекаются.
                </p>
                <div className="input-group">
                    <label>Чат (@username или id)</label>
                    <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="@my_chat или -1001234567890" />
                </div>
                <button type="button" className="action-btn btn-gold" disabled={busy || !chatInput.trim()} onClick={addChat}>
                    {busy ? '…' : 'Добавить чат'}
                </button>
                {err && <div className="action-result err">{err}</div>}
            </div>

            {items === null && !err && <div className="text-muted text-center">Загрузка…</div>}
            {items?.length === 0 && <div className="text-muted text-center">Чатов пока нет.</div>}
            {items?.map((c) => <GateChatCard key={c.chatId} chat={c} onChanged={load} />)}
        </>
    );
}

function GateChatCard({ chat, onChanged }: { chat: GateChat; onChanged: () => Promise<void> }) {
    const [channel, setChannel] = useState('');
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [verify, setVerify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
    const [status, setStatus] = useState<GateStatus | null>(null);

    const needsUrl = /^-100\d+$/.test(channel.trim());
    const run = async (fn: () => Promise<unknown>, okText?: string) => {
        if (busy) return;
        setBusy(true); setMsg(null);
        try { await fn(); if (okText) setMsg({ kind: 'ok', text: okText }); await onChanged(); }
        catch (e) { setMsg({ kind: 'err', text: gateErr(e) }); }
        finally { setBusy(false); }
    };

    const addChannel = () => run(async () => {
        await api.gateAddChannel(chat.chatId, {
            channel: channel.trim(), verify,
            ...(needsUrl && url.trim() ? { url: url.trim() } : {}),
            ...(title.trim() ? { title: title.trim() } : {}),
        });
        setChannel(''); setTitle(''); setUrl('');
    });

    const checkRights = () => run(async () => setStatus(await api.gateStatus(chat.chatId)));
    const r = status?.rights;

    return (
        <div className="info-card">
            <h3 className="popup-headline" style={{ marginBottom: 2 }}>{chat.title || 'Без названия'}</h3>
            <div className="text-muted" style={{ fontSize: 12 }}>
                {chat.username ? `@${chat.username} · ` : ''}id {chat.chatId}
            </div>
            <Kv k="Прошли проверку" v={chat.passes.toLocaleString('ru-RU')} />
            <Kv k="Ждут подписки сейчас" v={chat.pending.toLocaleString('ru-RU')} />
            <Kv k="Статус" v={!chat.botActive ? 'бот удалён из чата' : chat.channels.length === 0 ? 'выключено — нет каналов' : chat.enabled ? 'включено' : 'выключено'} />

            <div style={{ margin: '8px 0 4px', fontWeight: 700, fontSize: 13 }}>Обязательные каналы</div>
            {chat.channels.length === 0 && <div className="text-muted" style={{ fontSize: 12 }}>Пока пусто — чат открыт для всех.</div>}
            {chat.channels.map((ch) => {
                const live = status?.channels.find((x) => x.id === ch.id);
                return (
                    <div key={ch.id} className="info-row" style={{ alignItems: 'center', gap: 8 }}>
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ch.title}
                            <span className={ch.verify ? 'text-green' : 'text-muted'} style={{ fontSize: 11, marginLeft: 6 }}>
                                {ch.verify ? 'с проверкой' : 'без проверки'}
                            </span>
                            {live && !live.botAdmin && <span className="text-red" style={{ fontSize: 11, marginLeft: 6 }}>бот не админ</span>}
                            <div className="text-muted" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ch.url}</div>
                        </span>
                        <button type="button" className="action-btn btn-outline-gold" style={{ width: 'auto', padding: '4px 10px' }}
                            disabled={busy} onClick={() => run(() => api.gateRemoveChannel(chat.chatId, ch.id))}>✕</button>
                    </div>
                );
            })}

            <div className="input-group" style={{ marginTop: 10 }}>
                <label>Канал (@username, ссылка t.me/… или id)</label>
                <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="@channel или https://t.me/+AbC…" />
            </div>
            {needsUrl && (
                <div className="input-group">
                    <label>Ссылка для кнопки «Подписаться»</label>
                    <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://t.me/+AbC…" />
                </div>
            )}
            <div className="input-group">
                <label>Название на кнопке (необязательно)</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={64} />
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '6px 0' }}>
                <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} />
                Проверять подписку (бот должен быть админом канала)
            </label>
            {!verify && (
                <div className="text-muted" style={{ fontSize: 11, marginBottom: 6 }}>
                    Без проверки: кнопка показывается, но подписка не проверяется — для каналов, где копятся заявки.
                    Проверяемые каналы в этом же списке проверяются как обычно.
                </div>
            )}
            <button type="button" className="action-btn btn-gold" disabled={busy || !channel.trim()} onClick={addChannel}>
                {busy ? '…' : 'Добавить канал'}
            </button>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" className="action-btn btn-outline-gold" style={{ width: 'auto', padding: '6px 12px' }}
                    disabled={busy} onClick={() => run(() => api.gateSetEnabled(chat.chatId, !chat.enabled))}>
                    {chat.enabled ? 'Выключить' : 'Включить'}
                </button>
                <button type="button" className="action-btn btn-outline-gold" style={{ width: 'auto', padding: '6px 12px' }}
                    disabled={busy} onClick={checkRights}>Проверить права бота</button>
                <button type="button" className="action-btn btn-outline-gold" style={{ width: 'auto', padding: '6px 12px' }}
                    disabled={busy}
                    onClick={() => {
                        if (window.confirm('Все участники будут проверены заново. Продолжить?')) {
                            void run(async () => { await api.gateReset(chat.chatId); }, 'Проверки сброшены.');
                        }
                    }}>Проверить всех заново</button>
                <button type="button" className="action-btn btn-outline-gold" style={{ width: 'auto', padding: '6px 12px' }}
                    disabled={busy}
                    onClick={() => {
                        if (window.confirm('Перестать охранять этот чат и удалить его каналы?')) {
                            void run(() => api.gateRemoveChat(chat.chatId));
                        }
                    }}>Удалить чат</button>
            </div>

            {r !== undefined && status && (
                <div className={`action-result ${r?.admin && r.canDelete && r.canRestrict ? 'ok' : 'err'}`}>
                    {r === null && 'Не удалось проверить права бота.'}
                    {r && !r.admin && 'Бот не админ чата.'}
                    {r?.admin && (r.canDelete && r.canRestrict
                        ? 'Права бота в порядке.'
                        : `Не хватает прав: ${[!r.canDelete && 'удалять сообщения', !r.canRestrict && 'блокировать пользователей'].filter(Boolean).join(', ')}.`)}
                </div>
            )}
            {msg && <div className={`action-result ${msg.kind}`}>{msg.text}</div>}
        </div>
    );
}

/* -------------------------------------------------------------------- */

function Kv({ k, v }: { k: string; v: string }) {
    return (
        <div className="info-row">
            <span className="info-label">{k}</span>
            <span className="info-value">{v}</span>
        </div>
    );
}
function methodLabel(m: string) {
    return m === 'stars' ? '⭐ Stars'
        : m === 'ton' ? '💎 TON'
        : m === 'cryptobot' ? '🤖 CryptoBot'
        : m === 'gift' ? '🎁 Gift'
        : m;
}
function statusLabel(s: string) {
    return s === 'pending' ? 'Новая'
        : s === 'reviewing' ? 'В работе'
        : s === 'paid' ? 'Оплачена'
        : s === 'rejected' ? 'Отклонена'
        : s === 'failed' ? 'Ошибка'
        : s;
}
function ratePct(won: number, lost: number) {
    const total = won + lost;
    if (total === 0) return '—';
    return Math.round((won / total) * 100) + '%';
}
function fmtDate(iso: string) {
    try {
        const d = new Date(iso);
        return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}
function daysSince(iso: string): number {
    try {
        const ms = Date.now() - new Date(iso).getTime();
        return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
    } catch { return 0; }
}

// Re-exported just so the import path matches the original.
export { newIdempotencyKey };
